import {
  skipToken,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  ListCashReceiptsInput,
  ListCashReceiptsResponse,
  GetCashReceiptResponse,
  CashReceiptListItem,
  SyncCashReceiptsInput,
  SyncCashReceiptsResponse,
  OverrideCustomerInput,
  OverrideCustomerResponse,
  DeleteCashReceiptInput,
  DeleteCashReceiptResponse,
  CreateCashReceiptInput,
  CreateCashReceiptResponse,
  UpdateCashReceiptInput,
  UpdateCashReceiptResponse,
} from '@erp/rpc';
// AR Technical Demo (F0 / GEN-2256) — demo-mode shim. Delete with frontend/src/demo/ar.
import {
  arDemoActive,
  useArDemoFixture,
  withArDemoData,
} from '@/demo/ar/hook-shim';

/**
 * Query hook for the paginated cash receipt review queue.
 * Returns receipts with confidence from AiReasoningAudit.
 */
export function useArCashReceiptsList(filters: ListCashReceiptsInput = {}) {
  const demoData = useArDemoFixture(
    (s): ListCashReceiptsResponse => ({
      receipts: s.cashReceiptList,
      pagination: { total: s.cashReceiptList.length, limit: 100, offset: 0 },
    }),
  );
  const baseQuery = useQuery({
    ...orpc.accountsReceivable.arCashReceipts.list.queryOptions({
      input: filters,
    }),
    enabled: !arDemoActive(),
  });
  const query = withArDemoData(baseQuery, demoData);

  return {
    ...query,
    receipts: (query.data?.receipts ?? []) as CashReceiptListItem[],
    pagination: query.data?.pagination,
  } satisfies typeof query & {
    data?: ListCashReceiptsResponse;
    receipts: CashReceiptListItem[];
    pagination: ListCashReceiptsResponse['pagination'] | undefined;
  };
}

/**
 * Query hook for a single cash receipt detail with AI reasoning and linked payment.
 */
export function useArCashReceipt(id: string | undefined, enabled = true) {
  const demoData = useArDemoFixture((s) =>
    id ? { receipt: s.cashReceiptsById[id] ?? null } : undefined,
  );
  const baseQuery = useQuery({
    ...orpc.accountsReceivable.arCashReceipts.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled && !arDemoActive(),
  });
  const query = withArDemoData(baseQuery, demoData);

  return {
    ...query,
    receipt: query.data?.receipt ?? null,
  } satisfies typeof query & {
    data?: GetCashReceiptResponse;
    receipt: GetCashReceiptResponse['receipt'] | null;
  };
}

/**
 * Mutation hook to trigger the Plaid sync + cash receipt pipeline for a date range.
 * Invalidates the list cache on success so the review queue refreshes automatically.
 */
export function useSyncCashReceipts() {
  const queryClient = useQueryClient();

  return useMutation<SyncCashReceiptsResponse, Error, SyncCashReceiptsInput>({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.sync(input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arCashReceipts.list.queryOptions({
          input: {},
        }).queryKey,
      });
    },
  });
}

/**
 * Mutation hook to override the customer on a cash receipt.
 * Invalidates both the list and the specific receipt detail cache on success.
 */
export function useOverrideCashReceiptCustomer() {
  const queryClient = useQueryClient();

  return useMutation<OverrideCustomerResponse, Error, OverrideCustomerInput>({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.overrideCustomer(
        input,
      );
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arCashReceipts.list.queryOptions({
          input: {},
        }).queryKey,
      });
      await queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arCashReceipts.get.queryOptions({
          input: { id: variables.receiptId },
        }).queryKey,
      });
    },
  });
}

/**
 * Mutation hook to permanently delete a cash receipt. Used as an operator
 * escape hatch for badly-extracted rows. Invalidates the list cache on
 * success so the deleted row disappears from the review queue.
 */
export function useDeleteCashReceipt() {
  const queryClient = useQueryClient();

  return useMutation<DeleteCashReceiptResponse, Error, DeleteCashReceiptInput>({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.delete(input);
    },
    onSuccess: async () => {
      // Use `.key()` (prefix-only) so we invalidate the list regardless of
      // which filters the active query is using. `queryOptions({input:{}}).queryKey`
      // would only match queries called with empty filters.
      await queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arCashReceipts.list.key(),
      });
    },
  });
}

function invalidateCashReceiptQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCashReceipts.list.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.arCashReceipts.get.key(),
  });
}

/** Manually create a cash receipt (bank transaction). */
export function useCreateCashReceipt() {
  const queryClient = useQueryClient();
  return useMutation<CreateCashReceiptResponse, Error, CreateCashReceiptInput>({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.create(input);
    },
    onSuccess: () => invalidateCashReceiptQueries(queryClient),
  });
}

/** Edit a manual-origin cash receipt. */
export function useUpdateCashReceipt() {
  const queryClient = useQueryClient();
  return useMutation<UpdateCashReceiptResponse, Error, UpdateCashReceiptInput>({
    mutationFn: async (input) => {
      return rpcClient.accountsReceivable.arCashReceipts.update(input);
    },
    onSuccess: () => invalidateCashReceiptQueries(queryClient),
  });
}
